import { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { getMonthlyStats, getDailyStatsByRange } from '../../../services/pageViewService';
import { getContacts } from '../../../services/contactService';
import { getNewsStatsByRange } from '../../../services/newsService';

const formatNumber = (n: number) => {
  if (n >= 1000000) return (n / 1000000).toFixed(1) + 'M';
  if (n >= 1000) return (n / 1000).toFixed(1) + 'K';
  return String(n);
};

type Period = '1d' | '7d' | '30d' | 'custom';

const getDateRange = (period: Period, customFrom?: string, customTo?: string): { from: string; to: string } => {
  if (period === 'custom' && customFrom && customTo) {
    return { from: customFrom, to: customTo };
  }
  const to = new Date();
  const from = new Date(to);
  const days = period === '1d' ? 1 : period === '7d' ? 7 : 30;
  from.setDate(to.getDate() - days + 1);
  return {
    from: from.toISOString().slice(0, 10),
    to: to.toISOString().slice(0, 10),
  };
};

const getPrevDateRange = (period: Period, customFrom?: string, customTo?: string): { from: string; to: string } => {
  const { from, to } = getDateRange(period, customFrom, customTo);
  const fromDate = new Date(from);
  const toDate = new Date(to);
  const days = Math.ceil((toDate.getTime() - fromDate.getTime()) / (24 * 60 * 60 * 1000)) + 1;
  const toPrev = new Date(fromDate);
  toPrev.setDate(fromDate.getDate() - 1);
  const fromPrev = new Date(toPrev);
  fromPrev.setDate(toPrev.getDate() - days + 1);
  return {
    from: fromPrev.toISOString().slice(0, 10),
    to: toPrev.toISOString().slice(0, 10),
  };
};

const formatRangeLabel = (from: string, to: string): string => {
  const f = new Date(from);
  const t = new Date(to);
  const fm = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][f.getMonth()];
  const tm = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][t.getMonth()];
  return `${fm} ${String(f.getDate()).padStart(2, '0')} - ${tm} ${String(t.getDate()).padStart(2, '0')}`;
};

const DashboardOverview = () => {
  const [period, setPeriod] = useState<Period>('7d');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const datePickerRef = useRef<HTMLDivElement>(null);
  const [pageViewTotal, setPageViewTotal] = useState<number>(0);
  const [pageViewPrevTotal, setPageViewPrevTotal] = useState<number>(0);
  const [topPaths, setTopPaths] = useState<{ path: string; count: number }[]>([]);
  const [chartData, setChartData] = useState<{ day: number; date: string; label: string; count: number }[]>([]);
  const [loading, setLoading] = useState(true);
  const [recentContacts, setRecentContacts] = useState<any[]>([]);
  const [contactStats, setContactStats] = useState({ new: 0 });
  const [contactsLoading, setContactsLoading] = useState(true);
  const [newsPeriod, setNewsPeriod] = useState<Period>('7d');
  const [newsCustomFrom, setNewsCustomFrom] = useState('');
  const [newsCustomTo, setNewsCustomTo] = useState('');
  const [newsDatePickerOpen, setNewsDatePickerOpen] = useState(false);
  const newsDatePickerRef = useRef<HTMLDivElement>(null);
  const [newsStats, setNewsStats] = useState<{ stats: { date: string; label: string; count: number }[]; total: number }>({ stats: [], total: 0 });
  const [newsLoading, setNewsLoading] = useState(true);
  const [, setSearchParams] = useSearchParams();

  const newsRange = getDateRange(newsPeriod, newsCustomFrom, newsCustomTo);

  const { from, to } = getDateRange(period, customFrom, customTo);
  const endDate = new Date(to);
  const endYear = endDate.getFullYear();
  const endMonth = endDate.getMonth() + 1;

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (datePickerRef.current && !datePickerRef.current.contains(e.target as Node)) {
        setDatePickerOpen(false);
      }
    };
    if (datePickerOpen) document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [datePickerOpen]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (newsDatePickerRef.current && !newsDatePickerRef.current.contains(e.target as Node)) {
        setNewsDatePickerOpen(false);
      }
    };
    if (newsDatePickerOpen) document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [newsDatePickerOpen]);

  useEffect(() => {
    const fetchStats = async () => {
      if (period === 'custom' && (!customFrom || !customTo)) return;
      setLoading(true);
      try {
        const { from: fromPrev, to: toPrev } = getPrevDateRange(period, customFrom, customTo);
        const [daily, prevDaily, monthlyStats] = await Promise.all([
          getDailyStatsByRange(from, to),
          getDailyStatsByRange(fromPrev, toPrev),
          getMonthlyStats(endYear, endMonth),
        ]);
        const total = (daily || []).reduce((s, d) => s + d.count, 0);
        const prevTotal = (prevDaily || []).reduce((s, d) => s + d.count, 0);
        setPageViewTotal(total);
        setPageViewPrevTotal(prevTotal);
        setTopPaths((monthlyStats?.byPath || []).slice(0, 5));
        setChartData(daily || []);
      } catch {
        setPageViewTotal(0);
        setPageViewPrevTotal(0);
        setTopPaths([]);
        setChartData([]);
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, [period, customFrom, customTo]);

  useEffect(() => {
    const fetchContacts = async () => {
      try {
        const res = await getContacts({ page: 1, limit: 5 });
        setRecentContacts(res?.contacts ?? []);
        setContactStats(res?.stats ?? { new: 0 });
      } catch {
        setRecentContacts([]);
      } finally {
        setContactsLoading(false);
      }
    };
    fetchContacts();
  }, []);

  useEffect(() => {
    const fetchNewsStats = async () => {
      if (newsPeriod === 'custom' && (!newsCustomFrom || !newsCustomTo)) return;
      setNewsLoading(true);
      try {
        const res = await getNewsStatsByRange(newsRange.from, newsRange.to);
        setNewsStats(res);
      } catch {
        setNewsStats({ stats: [], total: 0 });
      } finally {
        setNewsLoading(false);
      }
    };
    fetchNewsStats();
  }, [newsPeriod, newsCustomFrom, newsCustomTo]);

  const pageViewChange = pageViewPrevTotal > 0
    ? (((pageViewTotal - pageViewPrevTotal) / pageViewPrevTotal) * 100).toFixed(0)
    : '0';
  const pageViewChangeType = Number(pageViewChange) >= 0 ? 'increase' : 'decrease';

  const stats = [
    {
      title: 'Tin tức',
      value: newsLoading ? '...' : formatNumber(newsStats.total ?? 0),
      change: '—',
      changeType: 'increase' as const,
      icon: 'ri-newspaper-line',
      color: 'bg-green-500'
    },
    {
      title: 'Liên hệ mới',
      value: contactsLoading ? '...' : String(contactStats.new),
      change: '—',
      changeType: 'increase' as const,
      icon: 'ri-message-line',
      color: 'bg-yellow-500'
    },
    {
      title: `Lượt truy cập (${period === '1d' ? '1 ngày' : period === '7d' ? '7 ngày' : period === '30d' ? '30 ngày' : 'tùy chọn'})`,
      value: loading ? '...' : formatNumber(pageViewTotal),
      change: loading ? '...' : (pageViewChangeType === 'increase' ? '+' : '') + pageViewChange + '%',
      changeType: pageViewChangeType,
      icon: 'ri-eye-line',
      color: 'bg-purple-500'
    }
  ];

  const formatContactDate = (dateStr: string) => {
    if (!dateStr) return '—';
    const d = new Date(dateStr);
    return d.toLocaleDateString('vi-VN', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  return (
    <div className="space-y-6">
      {/* Page Title */}
      <div>
        <h2 className="text-3xl font-bold text-gray-900 mb-2">Tổng quan</h2>
        <p className="text-gray-600">Thống kê và hoạt động gần đây của hệ thống</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {stats.map((stat, index) => (
          <div key={index} className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <div className={`w-12 h-12 ${stat.color} rounded-lg flex items-center justify-center`}>
                <i className={`${stat.icon} text-white text-xl`}></i>
              </div>
              <span className={`text-sm font-medium px-2 py-1 rounded-full ${
                stat.changeType === 'increase' 
                  ? 'text-green-700 bg-green-100' 
                  : 'text-red-700 bg-red-100'
              }`}>
                {stat.change}
              </span>
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-1">{stat.value}</h3>
            <p className="text-gray-600 text-sm">{stat.title}</p>
          </div>
        ))}
      </div>

      {/* Charts Row */}
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Traffic Chart - full width for more chart space */}
        <div className="lg:col-span-2 bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
            <h3 className="text-lg font-semibold text-gray-900">
              Lượt truy cập
            </h3>
            <div className="flex items-center gap-2">
              <div className="relative" ref={datePickerRef}>
                <button
                  type="button"
                  onClick={() => setDatePickerOpen(!datePickerOpen)}
                  className="flex items-center gap-1 px-3 py-2 text-sm border border-gray-300 rounded-lg text-gray-700 bg-white hover:bg-gray-50 cursor-pointer"
                >
                  {formatRangeLabel(from, to)}
                  <i className="ri-arrow-down-s-line text-lg"></i>
                </button>
                {datePickerOpen && (
                  <div className="absolute top-full left-0 mt-1 z-50 bg-white rounded-lg shadow-lg border border-gray-200 p-4 min-w-[280px]">
                    <div className="space-y-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-500 mb-1">Từ ngày</label>
                        <input
                          type="date"
                          value={period === 'custom' ? customFrom : from}
                          onChange={(e) => {
                            setCustomFrom(e.target.value);
                            setCustomTo((prev) => prev || to);
                            setPeriod('custom');
                          }}
                          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-500 mb-1">Đến ngày</label>
                        <input
                          type="date"
                          value={period === 'custom' ? customTo : to}
                          onChange={(e) => {
                            setCustomTo(e.target.value);
                            setCustomFrom((prev) => prev || from);
                            setPeriod('custom');
                          }}
                          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg"
                        />
                      </div>
                      <button
                        type="button"
                        onClick={() => setDatePickerOpen(false)}
                        className="w-full py-2 text-sm font-medium text-white bg-[#2A4B7C] rounded-lg hover:bg-[#2A4B7C]/90"
                      >
                        Áp dụng
                      </button>
                    </div>
                  </div>
                )}
              </div>
              <div className="flex rounded-lg overflow-hidden border border-gray-200">
                {(['1d', '7d', '30d'] as const).map((p) => (
                  <button
                    key={p}
                    type="button"
                    onClick={() => { setPeriod(p); setDatePickerOpen(false); }}
                    className={`px-3 py-2 text-sm font-medium transition-colors ${
                      period === p
                        ? 'bg-gray-200 text-gray-900'
                        : 'bg-white text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    {p}
                  </button>
                ))}
              </div>
            </div>
          </div>
          <div className="h-80 min-h-[280px]">
            {loading ? (
              <div className="h-full flex items-center justify-center text-gray-500">
                <i className="ri-loader-4-line animate-spin text-3xl"></i>
              </div>
            ) : chartData.length === 0 ? (
              <div className="h-full flex flex-col items-center justify-center text-gray-500">
                <i className="ri-line-chart-line text-4xl mb-2"></i>
                <p>Chưa có dữ liệu {formatRangeLabel(from, to)}</p>
                <p className="text-sm">Lượt truy cập sẽ được ghi nhận theo thời gian</p>
              </div>
            ) : (
              <ResponsiveContainer width="100%" height="100%" debounce={0}>
                <LineChart data={chartData} margin={{ top: 8, right: 8, left: 0, bottom: 24 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                  <XAxis
                    dataKey="label"
                    tick={{ fontSize: 10 }}
                    interval={chartData.length > 15 ? Math.floor(chartData.length / 12) : 0}
                  />
                  <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                  <Tooltip
                    content={({ active, payload }) =>
                      active && payload?.[0] ? (
                        <div className="bg-white px-3 py-2 rounded-lg shadow-lg border border-gray-200">
                          <p className="font-medium text-gray-900">Ngày {payload[0].payload.date}</p>
                          <p className="text-sm text-[#2A4B7C]">{payload[0].value} lượt truy cập</p>
                        </div>
                      ) : null
                    }
                  />
                  <Line
                    type="monotone"
                    dataKey="count"
                    stroke="#2A4B7C"
                    strokeWidth={2}
                    dot={{ fill: '#2A4B7C', r: 3 }}
                    activeDot={{ r: 5 }}
                    connectNulls
                  />
                </LineChart>
              </ResponsiveContainer>
            )}
          </div>
          {topPaths.length > 0 && (
            <div className="mt-4 pt-4 border-t">
              <p className="text-sm font-medium text-gray-700 mb-2">
                Top trang tháng {endMonth}/{endYear}
              </p>
              <ul className="space-y-1 text-sm">
                {topPaths.map((p, i) => (
                  <li key={i} className="flex justify-between">
                    <span className="text-gray-600 truncate max-w-[200px]">{p.path || '/'}</span>
                    <span className="font-medium text-gray-900">{formatNumber(p.count)}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>

      {/* Tin tức chart (left) | Liên hệ mới (right) */}
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Chart Tin tức - left */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Tin tức</h3>
            <div className="flex items-center gap-2">
              <div className="relative" ref={newsDatePickerRef}>
                <button
                  type="button"
                  onClick={() => setNewsDatePickerOpen(!newsDatePickerOpen)}
                  className="flex items-center gap-1 px-3 py-2 text-sm border border-gray-300 rounded-lg text-gray-700 bg-white hover:bg-gray-50 cursor-pointer"
                >
                  {formatRangeLabel(newsRange.from, newsRange.to)}
                  <i className="ri-arrow-down-s-line text-lg"></i>
                </button>
                {newsDatePickerOpen && (
                  <div className="absolute top-full left-0 mt-1 z-50 bg-white rounded-lg shadow-lg border border-gray-200 p-4 min-w-[280px]">
                    <div className="space-y-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-500 mb-1">Từ ngày</label>
                        <input
                          type="date"
                          value={newsPeriod === 'custom' ? newsCustomFrom : newsRange.from}
                          onChange={(e) => {
                            setNewsCustomFrom(e.target.value);
                            setNewsCustomTo((prev) => prev || newsRange.to);
                            setNewsPeriod('custom');
                          }}
                          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-500 mb-1">Đến ngày</label>
                        <input
                          type="date"
                          value={newsPeriod === 'custom' ? newsCustomTo : newsRange.to}
                          onChange={(e) => {
                            setNewsCustomTo(e.target.value);
                            setNewsCustomFrom((prev) => prev || newsRange.from);
                            setNewsPeriod('custom');
                          }}
                          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg"
                        />
                      </div>
                      <button
                        type="button"
                        onClick={() => setNewsDatePickerOpen(false)}
                        className="w-full py-2 text-sm font-medium text-white bg-[#2A4B7C] rounded-lg hover:bg-[#2A4B7C]/90"
                      >
                        Áp dụng
                      </button>
                    </div>
                  </div>
                )}
              </div>
              <div className="flex rounded-lg overflow-hidden border border-gray-200">
                {(['1d', '7d', '30d'] as const).map((p) => (
                  <button
                    key={p}
                    type="button"
                    onClick={() => { setNewsPeriod(p); setNewsDatePickerOpen(false); }}
                    className={`px-3 py-2 text-sm font-medium transition-colors ${
                      newsPeriod === p ? 'bg-gray-200 text-gray-900' : 'bg-white text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    {p}
                  </button>
                ))}
              </div>
              <button
                type="button"
                onClick={() => setSearchParams({ tab: 'news' })}
                className="text-[#2A4B7C] hover:text-[#2A4B7C]/80 text-sm font-medium"
              >
                Xem tất cả
              </button>
            </div>
          </div>
          <div className="h-64">
            {newsLoading ? (
              <div className="h-full flex items-center justify-center text-gray-500">
                <i className="ri-loader-4-line animate-spin text-3xl"></i>
              </div>
            ) : !newsStats.stats?.length ? (
              <div className="h-full flex flex-col items-center justify-center text-gray-500">
                <i className="ri-bar-chart-line text-4xl mb-2"></i>
                <p>Chưa có dữ liệu {formatRangeLabel(newsRange.from, newsRange.to)}</p>
              </div>
            ) : (
              <ResponsiveContainer width="100%" height="100%" debounce={0}>
                <LineChart data={newsStats.stats} margin={{ top: 8, right: 8, left: 0, bottom: 24 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                  <XAxis
                    dataKey="label"
                    tick={{ fontSize: 10 }}
                    interval={newsStats.stats.length > 15 ? Math.floor(newsStats.stats.length / 12) : 0}
                  />
                  <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                  <Tooltip
                    content={({ active, payload }) =>
                      active && payload?.[0] ? (
                        <div className="bg-white px-3 py-2 rounded-lg shadow-lg border border-gray-200">
                          <p className="font-medium text-gray-900">Ngày {payload[0].payload.date}</p>
                          <p className="text-sm text-[#22c55e]">{payload[0].value} bài viết</p>
                        </div>
                      ) : null
                    }
                  />
                  <Line
                    type="monotone"
                    dataKey="count"
                    stroke="#22c55e"
                    strokeWidth={2}
                    dot={{ fill: '#22c55e', r: 3 }}
                    activeDot={{ r: 5 }}
                    connectNulls
                  />
                </LineChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        {/* Liên hệ mới - right */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Liên hệ mới</h3>
          <button
            type="button"
            onClick={() => setSearchParams({ tab: 'contacts' })}
            className="text-[#2A4B7C] hover:text-[#2A4B7C]/80 text-sm font-medium"
          >
            Xem tất cả
          </button>
          </div>
          <div className="space-y-4">
          {contactsLoading ? (
            <div className="flex items-center justify-center py-12 text-gray-500">
              <i className="ri-loader-4-line animate-spin text-2xl"></i>
            </div>
          ) : recentContacts.length === 0 ? (
            <div className="py-12 text-center text-gray-500">
              <i className="ri-inbox-line text-4xl mb-2"></i>
              <p>Chưa có liên hệ nào</p>
            </div>
          ) : (
            recentContacts.map((contact) => (
              <div key={contact._id} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                <div className="w-10 h-10 bg-[#2A4B7C] rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-user-line text-white text-sm"></i>
                </div>
                <div className="flex-1 min-w-0">
                  <h4 className="font-medium text-gray-900 truncate">{contact.name}</h4>
                  <p className="text-sm text-gray-600 truncate">{contact.email}</p>
                  <p className="text-xs text-gray-500">{contact.serviceName || '—'}</p>
                </div>
                <div className="text-xs text-gray-500">
                  {formatContactDate(contact.createdAt)}
                </div>
              </div>
            ))
          )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardOverview;
